/**
 * « Mes déplacements » — M28 (retour Manon, mi-parcours ; maquette validée par
 * Henrico le 24/09).
 *
 * Tout au même endroit, en trois étapes par déplacement :
 *   1. Formation     — la formation acceptée préremplit objet, lieu et dates ;
 *   2. Ordre de mission — l'agent complète (transport, km), la RH valide ;
 *   3. Note de frais — « Déclarer mes frais » ouvre le brouillon rattaché.
 * Vit dans « Mes demandes », sous les demandes de formation : fini le « de
 * fenêtre en fenêtre ».
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Check, FileText, Receipt, Plus } from 'lucide-react';
import { Card, Badge, Button, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import {
  api, ApiException, type DeplacementDTO, type DeplacementInput, type TransportDeplacement,
  type DemandeFormationDTO,
} from '@/lib/api';

const TRANSPORTS: Record<TransportDeplacement, string> = {
  vehicule_service: 'Véhicule de service', vehicule_personnel: 'Véhicule personnel',
  train: 'Train', covoiturage: 'Covoiturage', autre: 'Autre',
};
const fr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const VIDE: DeplacementInput = { objet: '', lieu: '', dateDepart: '', dateRetour: '', transport: 'vehicule_service', kmEstimes: null, commentaire: '' };

/** Une étape de la frise : faite, en cours ou à venir. */
function Etape({ icone: Icone, titre, detail, etat }: {
  icone: typeof Check; titre: string; detail: string; etat: 'fait' | 'encours' | 'avenir' | 'bloque';
}) {
  const cls = etat === 'fait' ? 'border-success/40 text-success'
    : etat === 'encours' ? 'border-rh/60 text-rh' : etat === 'bloque' ? 'border-urgent/40 text-urgent' : 'border-border text-ink-subtle';
  return (
    <div className={`min-w-0 flex-1 rounded-r-md border px-2.5 py-1.5 ${cls}`}>
      <p className="flex items-center gap-1 text-2xs"><Icone className="h-3.5 w-3.5" aria-hidden="true" /> {titre}</p>
      <p className="truncate text-xs text-ink">{detail}</p>
    </div>
  );
}

export function MesDeplacementsCard({ demandesFormation, onChange }: {
  demandesFormation: DemandeFormationDTO[];
  onChange?: () => void;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<DeplacementDTO[]>([]);
  const [dialogue, setDialogue] = useState(false);
  const [f, setF] = useState<DeplacementInput>(VIDE);
  const [err, setErr] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try { setItems(await api.rh.mesDeplacements()); } catch { setItems([]); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  // Les formations acceptées qui n'ont pas encore leur déplacement.
  const aPreparer = demandesFormation.filter((d) => d.statut === 'acceptee'
    && !items.some((x) => x.demandeFormationId === d.id && x.statut !== 'annule' && x.statut !== 'refuse'));

  async function ouvrir(demandeFormationId?: string) {
    setErr(null);
    if (!demandeFormationId) { setF(VIDE); setDialogue(true); return; }
    try {
      const pre = await api.rh.preremplirDeplacement({ demandeFormationId });
      setF({ ...VIDE, ...pre, transport: 'vehicule_service' });
      setDialogue(true);
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Préremplissage impossible'); }
  }

  async function envoyer() {
    if (f.objet.trim().length < 3 || f.lieu.trim().length < 2 || !f.dateDepart || !f.dateRetour) {
      setErr('Objet, lieu et dates sont nécessaires'); return;
    }
    if (f.dateRetour < f.dateDepart) { setErr('Le retour doit suivre le départ'); return; }
    setEnvoi(true); setErr(null);
    try {
      await api.rh.creerDeplacement({ ...f, objet: f.objet.trim(), lieu: f.lieu.trim(), commentaire: f.commentaire?.trim() || null });
      setDialogue(false); await charger(); onChange?.();
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Envoi impossible'); }
    finally { setEnvoi(false); }
  }

  async function declarerFrais(id: string) {
    setErr(null);
    try {
      await api.rh.declarerFraisDeplacement(id);
      navigate('/rh?vue=perso&onglet=notesfrais');   // la note brouillon rattachée y attend
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Création de la note impossible'); }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <Route className="h-5 w-5 text-rh" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-ink">Mes déplacements</h2>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => void ouvrir()}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Autre déplacement
        </Button>
      </div>
      {err && <p className="mb-2 text-xs text-urgent" role="alert">{err}</p>}

      {aPreparer.map((d) => (
        <div key={d.id} className="mb-2 flex flex-wrap items-center gap-2 rounded-r-md border border-rh/40 bg-rh/5 px-3 py-2 text-sm">
          <Check className="h-4 w-4 text-success" aria-hidden="true" />
          <span className="text-ink">Formation acceptée : <b>{d.intitule}</b></span>
          <Button size="sm" variant="rh" className="ml-auto" onClick={() => void ouvrir(d.id)}>Préparer mon déplacement</Button>
        </div>
      ))}

      {items.length === 0 && aPreparer.length === 0 && (
        <p className="text-sm text-ink-subtle">Aucun déplacement. Une formation acceptée vous proposera d'en préparer un.</p>
      )}

      <ul className="space-y-2">
        {items.filter((x) => x.statut !== 'annule').map((d) => (
          <li key={d.id} className="rounded-r-md border border-border/60 p-3">
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-ink">{d.objet}</span>
              <span className="text-xs text-ink-muted">{d.lieu} · {fr(d.dateDepart)}{d.dateRetour !== d.dateDepart ? ` → ${fr(d.dateRetour)}` : ''} · {TRANSPORTS[d.transport]}</span>
            </div>
            <div className="flex flex-col gap-1.5 sm:flex-row">
              <Etape icone={Check} titre="1 · Formation" etat="fait"
                detail={d.demandeFormationId || d.formationId ? 'Acceptée' : 'Hors formation'} />
              <Etape icone={FileText} titre="2 · Ordre de mission"
                etat={d.statut === 'valide' ? 'fait' : d.statut === 'refuse' ? 'bloque' : 'encours'}
                detail={d.statut === 'valide' ? `Validé${d.valideParNom ? ` par ${d.valideParNom}` : ''}` : d.statut === 'refuse' ? `Refusé${d.motifRefus ? ` — ${d.motifRefus}` : ''}` : 'En attente de la RH'} />
              <Etape icone={Receipt} titre="3 · Note de frais"
                etat={d.noteFraisStatut && d.noteFraisStatut !== 'brouillon' ? 'fait' : d.statut === 'valide' ? 'encours' : 'avenir'}
                detail={d.noteFraisStatut ? (d.noteFraisStatut === 'brouillon' ? 'Brouillon à compléter' : 'Envoyée') : 'Au retour'} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {d.statut === 'valide' && (
                <Button size="sm" variant="rh" onClick={() => void declarerFrais(d.id)}>
                  {d.noteFraisId ? 'Ouvrir ma note de frais' : 'Déclarer mes frais'}
                </Button>
              )}
              {d.statut === 'a_valider' && (
                <Button size="sm" variant="ghost" onClick={() => void api.rh.annulerDeplacement(d.id).then(charger)}>Annuler</Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={dialogue} onClose={() => setDialogue(false)} title="Préparer mon ordre de mission"
        footer={<><Button variant="ghost" onClick={() => setDialogue(false)}>Annuler</Button><Button variant="rh" loading={envoi} onClick={() => void envoyer()}>Envoyer à la RH</Button></>}>
        <div className="space-y-3">
          {err && <p className="text-xs text-urgent" role="alert">{err}</p>}
          <label className="block text-xs text-ink">Objet
            <Input className="mt-1" value={f.objet} maxLength={300} onChange={(e) => setF({ ...f, objet: e.target.value })} />
          </label>
          <label className="block text-xs text-ink">Lieu
            <Input className="mt-1" value={f.lieu} maxLength={300} placeholder="Ex : CNFPT La Rochelle" onChange={(e) => setF({ ...f, lieu: e.target.value })} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-ink">Départ
              <Input className="mt-1" type="date" value={f.dateDepart} onChange={(e) => setF({ ...f, dateDepart: e.target.value, dateRetour: f.dateRetour || e.target.value })} />
            </label>
            <label className="block text-xs text-ink">Retour
              <Input className="mt-1" type="date" value={f.dateRetour} onChange={(e) => setF({ ...f, dateRetour: e.target.value })} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-ink">Transport
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={f.transport}
                onChange={(e) => setF({ ...f, transport: e.target.value as TransportDeplacement })}>
                {(Object.keys(TRANSPORTS) as TransportDeplacement[]).map((t) => <option key={t} value={t}>{TRANSPORTS[t]}</option>)}
              </select>
            </label>
            <label className="block text-xs text-ink">Km aller-retour (estimés)
              <Input className="mt-1" type="number" min={0} max={20000} value={f.kmEstimes ?? ''}
                onChange={(e) => setF({ ...f, kmEstimes: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })} />
            </label>
          </div>
          <label className="block text-xs text-ink">Commentaire (facultatif)
            <Input className="mt-1" value={f.commentaire ?? ''} maxLength={2000} onChange={(e) => setF({ ...f, commentaire: e.target.value })} />
          </label>
          <p className="text-xs text-ink-subtle">Une fois validé par la RH, « Déclarer mes frais » ouvrira votre note de frais déjà rattachée à ce déplacement.</p>
        </div>
      </Dialog>
    </Card>
  );
}
